/**
 * Jobber CRM Management Page
 * 
 * Comprehensive management interface for Jobber CRM integration
 * including customer sync, job tracking, and invoice management
 */

import React, { useState } from 'react'
import { 
  Users, 
  Briefcase, 
  FileText, 
  DollarSign, 
  RefreshCw, 
  Settings,
  AlertCircle,
  CheckCircle,
  TrendingUp,
  Calendar,
  Filter,
  Search,
  Download
} from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Progress } from '@/components/ui/progress'
import { useJobberIntegrations } from '@/features/integrations/hooks/useIntegrations'
import { integrationUtils } from '@/features/integrations/utils/integrationUtils'

// Mock data for Jobber sync statistics
const mockJobberStats = {
  customers: {
    total: 1247,
    synced: 1203,
    pending: 44,
    errors: 0,
    lastSync: '2025-06-10T10:30:00Z'
  },
  jobs: {
    total: 856,
    synced: 823,
    pending: 28,
    errors: 5,
    lastSync: '2025-06-10T10:25:00Z'
  },
  invoices: {
    total: 634,
    synced: 628,
    pending: 4,
    errors: 2,
    lastSync: '2025-06-10T10:35:00Z'
  },
  estimates: {
    total: 432,
    synced: 425,
    pending: 6,
    errors: 1,
    lastSync: '2025-06-10T10:28:00Z'
  }
}

const mockJobberCustomers = [
  {
    id: 'cust_001',
    jobber_id: '12345',
    first_name: 'John',
    last_name: 'Smith',
    email: 'john@email.com',
    phone: '416-555-0123',
    address: '123 Main St, Toronto, ON',
    status: 'active',
    created_in_jobber: '2024-12-15T10:00:00Z',
    sync_status: 'synced',
    total_jobs: 3,
    total_value: 5650
  },
  {
    id: 'cust_002',
    jobber_id: '12346',
    first_name: 'Sarah',
    last_name: 'Johnson',
    email: 'sarah@email.com',
    phone: '416-555-0124',
    address: '456 Oak Ave, Hamilton, ON',
    status: 'active',
    created_in_jobber: '2024-12-10T14:30:00Z',
    sync_status: 'pending',
    total_jobs: 1,
    total_value: 2800
  },
  {
    id: 'cust_003',
    jobber_id: '12347',
    first_name: 'Mike',
    last_name: 'Wilson',
    email: 'mike@email.com',
    phone: '905-555-0125',
    address: '789 Pine Rd, Brampton, ON',
    status: 'active',
    created_in_jobber: '2024-12-08T09:15:00Z',
    sync_status: 'synced',
    total_jobs: 5,
    total_value: 8900
  }
]

const mockJobberJobs = [
  {
    id: 'job_001',
    jobber_id: 'job_12345',
    title: 'Brick Restoration - Main Street',
    customer_name: 'John Smith',
    status: 'completed',
    scheduled_start: '2025-01-15T09:00:00Z',
    actual_end: '2025-01-17T16:00:00Z',
    total_amount: 4500,
    sync_status: 'synced'
  },
  {
    id: 'job_002',
    jobber_id: 'job_12346',
    title: 'Chimney Repair - Oak Avenue',
    customer_name: 'Sarah Johnson',
    status: 'in_progress',
    scheduled_start: '2025-01-20T08:00:00Z',
    total_amount: 2800,
    sync_status: 'synced'
  },
  {
    id: 'job_003',
    jobber_id: 'job_12347',
    title: 'Tuckpointing - Pine Road',
    customer_name: 'Mike Wilson',
    status: 'scheduled',
    scheduled_start: '2025-01-25T09:30:00Z',
    total_amount: 3200,
    sync_status: 'pending'
  }
]

export default function JobberManagement() {
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [syncFilter, setSyncFilter] = useState('all')
  const [activeTab, setActiveTab] = useState('overview')

  const { jobberIntegrations, isLoading } = useJobberIntegrations()

  const filteredCustomers = mockJobberCustomers.filter(customer => {
    const matchesSearch = 
      customer.first_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      customer.last_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      customer.email.toLowerCase().includes(searchTerm.toLowerCase())
    
    const matchesStatus = statusFilter === 'all' || customer.status === statusFilter
    const matchesSync = syncFilter === 'all' || customer.sync_status === syncFilter

    return matchesSearch && matchesStatus && matchesSync
  })

  const filteredJobs = mockJobberJobs.filter(job => {
    const matchesSearch = 
      job.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      job.customer_name.toLowerCase().includes(searchTerm.toLowerCase())
    
    const matchesStatus = statusFilter === 'all' || job.status === statusFilter
    const matchesSync = syncFilter === 'all' || job.sync_status === syncFilter

    return matchesSearch && matchesStatus && matchesSync
  })

  const getSyncProgress = (data: any) => {
    return Math.round((data.synced / data.total) * 100)
  }

  const getStatusBadge = (status: string, type: 'sync' | 'job' = 'sync') => {
    if (type === 'sync') {
      switch (status) {
        case 'synced':
          return <Badge variant="default" className="bg-green-100 text-green-800">Synced</Badge>
        case 'pending':
          return <Badge variant="secondary" className="bg-yellow-100 text-yellow-800">Pending</Badge>
        case 'error':
          return <Badge variant="destructive">Error</Badge>
        default:
          return <Badge variant="outline">Unknown</Badge>
      }
    } else {
      switch (status) {
        case 'completed':
          return <Badge variant="default" className="bg-green-100 text-green-800">Completed</Badge>
        case 'in_progress':
          return <Badge variant="secondary" className="bg-blue-100 text-blue-800">In Progress</Badge>
        case 'scheduled':
          return <Badge variant="outline" className="bg-purple-100 text-purple-800">Scheduled</Badge>
        case 'cancelled':
          return <Badge variant="destructive">Cancelled</Badge>
        default:
          return <Badge variant="outline">Unknown</Badge>
      }
    }
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-3xl font-bold">Jobber CRM Integration</h1>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <Card key={i} className="animate-pulse">
              <CardContent className="p-6">
                <div className="h-4 bg-gray-200 rounded w-1/2 mb-2"></div>
                <div className="h-8 bg-gray-200 rounded w-1/4"></div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Jobber CRM Integration</h1>
          <p className="text-muted-foreground mt-1">
            Manage customer data, jobs, invoices, and estimates from Jobber CRM
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline">
            <Settings className="w-4 h-4 mr-2" />
            Settings
          </Button>
          <Button>
            <RefreshCw className="w-4 h-4 mr-2" />
            Sync Now
          </Button>
        </div>
      </div>

      {/* Integration Status */}
      {jobberIntegrations.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-600" />
              Jobber CRM Connected
            </CardTitle>
            <CardDescription>
              Last synced: {integrationUtils.formatRelativeTime(jobberIntegrations[0].last_sync || new Date().toISOString())}
            </CardDescription>
          </CardHeader>
        </Card>
      )}

      {jobberIntegrations.length === 0 && (
        <Card>
          <CardContent className="p-12 text-center">
            <AlertCircle className="w-12 h-12 text-orange-500 mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">No Jobber Integration</h3>
            <p className="text-muted-foreground mb-4">
              Connect your Jobber CRM account to start syncing customer data and jobs.
            </p>
            <Button>Connect Jobber CRM</Button>
          </CardContent>
        </Card>
      )}

      {/* Sync Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Customers</p>
                <p className="text-2xl font-bold">{mockJobberStats.customers.synced.toLocaleString()}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  of {mockJobberStats.customers.total.toLocaleString()} total
                </p>
              </div>
              <div className="bg-blue-50 p-3 rounded-full">
                <Users className="w-6 h-6 text-blue-600" />
              </div>
            </div>
            <div className="mt-4">
              <Progress value={getSyncProgress(mockJobberStats.customers)} className="h-2" />
              <p className="text-xs text-muted-foreground mt-2">
                {getSyncProgress(mockJobberStats.customers)}% synced
              </p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Jobs</p>
                <p className="text-2xl font-bold">{mockJobberStats.jobs.synced.toLocaleString()}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  of {mockJobberStats.jobs.total.toLocaleString()} total
                </p>
              </div>
              <div className="bg-green-50 p-3 rounded-full">
                <Briefcase className="w-6 h-6 text-green-600" />
              </div>
            </div>
            <div className="mt-4">
              <Progress value={getSyncProgress(mockJobberStats.jobs)} className="h-2" />
              <p className="text-xs text-muted-foreground mt-2">
                {getSyncProgress(mockJobberStats.jobs)}% synced
              </p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Invoices</p>
                <p className="text-2xl font-bold">{mockJobberStats.invoices.synced.toLocaleString()}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  of {mockJobberStats.invoices.total.toLocaleString()} total
                </p>
              </div>
              <div className="bg-orange-50 p-3 rounded-full">
                <FileText className="w-6 h-6 text-orange-600" />
              </div>
            </div>
            <div className="mt-4">
              <Progress value={getSyncProgress(mockJobberStats.invoices)} className="h-2" />
              <p className="text-xs text-muted-foreground mt-2">
                {getSyncProgress(mockJobberStats.invoices)}% synced
              </p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Estimates</p>
                <p className="text-2xl font-bold">{mockJobberStats.estimates.synced.toLocaleString()}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  of {mockJobberStats.estimates.total.toLocaleString()} total
                </p>
              </div>
              <div className="bg-purple-50 p-3 rounded-full">
                <DollarSign className="w-6 h-6 text-purple-600" />
              </div>
            </div>
            <div className="mt-4">
              <Progress value={getSyncProgress(mockJobberStats.estimates)} className="h-2" />
              <p className="text-xs text-muted-foreground mt-2">
                {getSyncProgress(mockJobberStats.estimates)}% synced
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Data Management Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="customers">Customers</TabsTrigger>
          <TabsTrigger value="jobs">Jobs</TabsTrigger>
          <TabsTrigger value="invoices">Invoices</TabsTrigger>
          <TabsTrigger value="sync">Sync Status</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Recent Activity</CardTitle>
                <CardDescription>Latest sync activity and updates</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                    <div className="flex-1">
                      <p className="text-sm font-medium">Customer sync completed</p>
                      <p className="text-xs text-muted-foreground">5 minutes ago</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                    <div className="flex-1">
                      <p className="text-sm font-medium">New job created: Chimney Repair</p>
                      <p className="text-xs text-muted-foreground">12 minutes ago</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
                    <div className="flex-1">
                      <p className="text-sm font-medium">Invoice payment received</p>
                      <p className="text-xs text-muted-foreground">25 minutes ago</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Sync Health</CardTitle>
                <CardDescription>Integration performance metrics</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm">Overall Health</span>
                    <Badge variant="default" className="bg-green-100 text-green-800">Excellent</Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm">Sync Success Rate</span>
                    <span className="text-sm font-medium">98.5%</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm">Average Sync Time</span>
                    <span className="text-sm font-medium">2.3 minutes</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm">Last Full Sync</span>
                    <span className="text-sm font-medium">6 hours ago</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="customers" className="space-y-6">
          {/* Filters */}
          <Card>
            <CardContent className="p-6">
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="flex-1">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <Input
                      placeholder="Search customers..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                </div>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-[160px]">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Statuses</SelectItem>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={syncFilter} onValueChange={setSyncFilter}>
                  <SelectTrigger className="w-[160px]">
                    <SelectValue placeholder="Sync Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="synced">Synced</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="error">Error</SelectItem>
                  </SelectContent>
                </Select>
                <Button variant="outline">
                  <Download className="w-4 h-4 mr-2" />
                  Export
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Customer List */}
          <div className="grid gap-4">
            {filteredCustomers.map((customer) => (
              <Card key={customer.id}>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                        <span className="text-blue-600 font-semibold">
                          {customer.first_name[0]}{customer.last_name[0]}
                        </span>
                      </div>
                      <div>
                        <h3 className="font-semibold">
                          {customer.first_name} {customer.last_name}
                        </h3>
                        <p className="text-sm text-muted-foreground">{customer.email}</p>
                        <p className="text-sm text-muted-foreground">{customer.phone}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="flex items-center gap-2 mb-2">
                        {getStatusBadge(customer.sync_status)}
                        {getStatusBadge(customer.status, 'job')}
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {customer.total_jobs} jobs • ${customer.total_value.toLocaleString()}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Added {integrationUtils.formatRelativeTime(customer.created_in_jobber)}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="jobs" className="space-y-6">
          {/* Job List */}
          <div className="grid gap-4">
            {filteredJobs.map((job) => (
              <Card key={job.id}>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                        <Briefcase className="w-5 h-5 text-green-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold">{job.title}</h3>
                        <p className="text-sm text-muted-foreground">Customer: {job.customer_name}</p>
                        <p className="text-sm text-muted-foreground">
                          Scheduled: {new Date(job.scheduled_start).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="flex items-center gap-2 mb-2">
                        {getStatusBadge(job.sync_status)}
                        {getStatusBadge(job.status, 'job')}
                      </div>
                      <p className="text-lg font-semibold">
                        ${job.total_amount.toLocaleString()}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="invoices">
          <Card>
            <CardContent className="p-12 text-center">
              <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">Invoices Coming Soon</h3>
              <p className="text-muted-foreground">
                Invoice management and tracking will be available in the next update.
              </p>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="sync">
          <Card>
            <CardContent className="p-12 text-center">
              <RefreshCw className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">Sync Status Details</h3>
              <p className="text-muted-foreground">
                Detailed sync status and error reporting will be available soon.
              </p>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  )
}